import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import ConfirmChangeStatus from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Branch } from "../../../../../../models/branch";
import { Row } from "../../../../../../models/shipeezi.table";
import { Warehouse } from "../../../../../../models/warehouse";
import { Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import BranchModal from "./BranchModal";
import useStyles from "./styles";
import { useActions } from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean;
    create: boolean;
  };
};

const BranchPage = (props: Props) => {
  const { permission } = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const {
    variables,
    createBranch,
    updateBranch,
    setPageSize,
    setBranch,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status === Status.ACTIVE ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const editBranch = (index: number) => {
    if (variables.hasErrors !== undefined) setHasErrors(undefined);
    let branchList: Branch[] = [];

    try {
      branchList = JSON.parse(JSON.stringify(variables.branches));
    } catch {
      branchList = [...variables.branches];
    }
    const findBranch = branchList.find((branch) => Number(branch.id) === Number(index));

    if (findBranch) {
      setBranch(findBranch);
      setIsOnViewMode(false);
      setOpenModal(true);
    }
  };

  const viewBranch = (index: number) => (
    <Tooltip title="View" placement="top">
      <div>
        <FiEye
          className={classes.button}
          size={18}
          onClick={() => {
            if (variables.hasErrors !== undefined) setHasErrors(undefined);
            const findBranch = variables.branches.find(
              (branch) => Number(branch.id) === Number(index)
            );

            if (findBranch) {
              setBranch(findBranch);
              setIsOnViewMode(true);
              setOpenModal(true);
            }
          }}
        />
      </div>
    </Tooltip>
  );

  const changeBranchStatus = (index: number) => {
    const findBranch = variables.branches.find((branch) => Number(branch.id) === Number(index));

    if (findBranch) {
      updateBranch({
        id: Number(index),
        branch: {
          ...findBranch,
          status: findBranch.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderChangeAllStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeAllStatusButton}
          data-cy="inactiveStatus"
          onClick={() => setOpenConfirmation(true)}
          customColor={Color.INFO}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }

    return null;
  };

  const handleSaveBranchSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, variables.branches);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    { name: "name", title: translate("name") },
    {
      name: "warehouses",
      title: translate("warehouses"),
      formatText: (warehouses: Warehouse[]) => {
        if (warehouses) return warehouses.map((warehouse) => warehouse.name).join(" / ");

        return "";
      },
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleActive", "handleNewRecord"],
    },
    handleNewRecord:
      permission && isShowAction(currentUser, permission.create)
        ? {
            text: translate("create"),
            action: () => {
              if (variables.hasErrors !== undefined) setHasErrors(undefined);
              setIsOnViewMode(false);
              setBranch(undefined);
              setOpenModal(true);
            },
            tooltip: translate("create"),
          }
        : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        {
          name: translate("code"),
          column: "code",
        },
        {
          name: translate("name"),
          column: "name",
        },
      ],
      onChange: handleFilter,
    },
    handleActive: renderChangeAllStatus(),
  };

  useEffect(() => {
    if (variables.hasErrors !== undefined && !variables.hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.success.message.branches.id",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [variables.hasErrors]);

  return (
    <div data-cy="branchDataList" className={classes.wrapper}>
      {/* <header className="mb-4">
        <Breadcrumb
          items={[
            { title: translate("configurations") },
            {
              title: translate("branches"),
              color: "#465869",
              dataCy: "breadcrumbsSubTitle",
            },
          ]}
        />
      </header> */}

      <ShipeeziNewGrid
        data={variables.branches}
        columns={columns}
        totalItems={variables.total}
        loadingData={variables.isFetching || variables.isCreating || variables.isUpdating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={variables.pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={variables.page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveBranchSelected(rows)}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChange"],
          handleEdit:
            permission && isShowAction(currentUser, permission.edit)
              ? (branchId) => editBranch(Number(branchId))
              : undefined,
          handleChange: (branchId) => changeBranchStatus(Number(branchId)),
        }}
        rowActionsExtra={[{ title: "View", key: "handleView", buttonAndAction: viewBranch }]}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />

      {openModal && (
        <BranchModal
          branchToUpdate={variables.branch}
          open={openModal}
          hasErrors={variables.hasErrors}
          isLoading={variables.isUpdating || variables.isCreating}
          isOnViewMode={isOnViewMode}
          closeModal={() => setOpenModal(false)}
          submit={(branch: Branch) => {
            if (branch.id) updateBranch({ id: branch.id, branch });
            else
              createBranch({
                ...branch,
                companyId: currentUser?.companyId,
              });
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          closeModal={() => setOpenConfirmation(false)}
          rows={rows}
          isLoading={variables.isUpdating}
          hasErrors={variables.hasErrors}
          update={(rowId, status) => {
            if (rowId) {
              updateBranch({ id: rowId, branch: { status } as Branch });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default BranchPage;
